import React, { useState } from "react";

import axios from "axios";
import queryString from "query-string";
import { styled } from "styled-components";

import { colorTheme, gridSpacing, slugifyUrl } from "@utils";

import type { FilterModalProps } from "./FilterModalProps";
import ViewSummary from "./ViewSummary";
import Button from "../../Button/Button";
import Callout from "../../Callout/Callout";
import Modal from "../../Modal/Modal";
import RadioButton from "../../RadioButton/RadioButton";
import Switch from "../../Switch/Switch";
import Text from "../../Text/Text";
import TextArea from "../../TextArea/TextArea";

const Label = styled.div`
  margin: ${gridSpacing[4]}px 0 ${gridSpacing[2]}px;
  font-weight: 600;
`;

const Choices = styled.div`
  display: flex;
  gap: ${gridSpacing[3]}px;
`;

const Choice = styled.div<{ $selected: boolean }>`
  display: flex;
  flex: 1;
  align-items: flex-start;
  gap: ${gridSpacing[1]}px;
  padding: ${gridSpacing[2]}px ${gridSpacing[3]}px ${gridSpacing[3]}px
    ${gridSpacing[1]}px;
  border: 1px solid
    ${({ $selected }) =>
      $selected ? colorTheme("primary") : colorTheme("neutralL4")};
  border-radius: 6px;
  background: ${({ $selected }) =>
    $selected ? colorTheme("primaryL5") : colorTheme("white")};
  cursor: pointer;

  small {
    display: block;
    margin-top: ${gridSpacing[1]}px;
    color: ${colorTheme("neutralL1")};
    font-size: 13px;
  }
`;

const Preview = styled.div`
  padding: ${gridSpacing[3]}px ${gridSpacing[4]}px;
  border: 1px solid ${colorTheme("neutralL4")};
  border-radius: 6px;
  background: ${colorTheme("neutralL6")};
`;

const Hint = styled.div`
  margin-top: ${gridSpacing[1]}px;
  color: ${colorTheme("neutralL1")};
  font-size: 13px;
`;

type ErrorProps = {
  name?: string;
  main?: string;
};

const SaveFilterModal = ({
  app,
  model,
  pageKey,
  query,
  savedFilters,
  constantFilters,
  close,
  saveFilter,
  tableLayout,
  describeQuery,
}: FilterModalProps) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  // New views start private; the owner can share them now or later
  const [isPublic, setIsPublic] = useState(false);
  const [includeLayout, setIncludeLayout] = useState(!!tableLayout);
  const [errors, setErrors] = useState<ErrorProps>({});
  const [saving, setSaving] = useState(false);

  // Constant filters are always applied by the page, so they aren't part of the view
  const queryObject = queryString.parse(query);
  Object.keys(queryObject).forEach((key) => {
    if (constantFilters?.some((filters) => key in filters)) {
      delete queryObject[key];
    }
  });
  const sendQuery = queryString.stringify(queryObject);
  const layout = includeLayout && tableLayout ? tableLayout.snapshot() : null;

  const validate = () => {
    const _errors: ErrorProps = {};
    const trimmed = name.trim();

    if (trimmed.length === 0) {
      _errors.name = "Name required";
    } else if (trimmed.length < 3) {
      _errors.name = "Name must be 3 or more characters long";
    } else if (trimmed.length > 200) {
      _errors.name = "Name must be less than 200 characters long";
    } else if (
      savedFilters.some(
        (view) => view.name.toLowerCase() === trimmed.toLowerCase(),
      )
    ) {
      _errors.name = "Name already exists";
    }

    if (!sendQuery && !layout) {
      _errors.main = tableLayout
        ? "Add a filter or include the table layout to save a view"
        : "No filters to save";
    } else if (Object.keys(_errors).length) {
      _errors.main = "Not all fields have been satisfied";
    }

    setErrors(_errors);
    return Object.keys(_errors).length === 0;
  };

  const submit = () => {
    if (!validate()) return;
    setSaving(true);
    axios
      .post("search/filters", {
        name: name.trim(),
        description,
        public: isPublic,
        app,
        model,
        key: pageKey,
        query: sendQuery,
        slug: slugifyUrl(name.trim()),
        table_state: layout,
      })
      .then((res) => saveFilter(res.data.result))
      .catch((err) => {
        console.error(err);
        setErrors({ main: "Error saving view" });
      })
      .finally(() => setSaving(false));
  };

  return (
    <>
      <Text
        required
        mb
        label="Name"
        testId="save-view-name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        error={errors.name}
      />
      <TextArea
        rows={2}
        label="Description"
        value={description}
        placeholder="Enter Description"
        onChange={(e) => setDescription(e.target.value)}
      />
      {tableLayout && (
        <>
          <Label>What to save</Label>
          <Choices>
            <Choice
              $selected={!includeLayout}
              onClick={() => setIncludeLayout(false)}
              data-testid="save-filters-only"
            >
              <RadioButton
                size={18}
                checked={!includeLayout}
                onChange={() => setIncludeLayout(false)}
              />
              <div>
                Filters only
                <small>
                  Columns, sort and rows per page stay as each person has them
                </small>
              </div>
            </Choice>
            <Choice
              $selected={includeLayout}
              onClick={() => setIncludeLayout(true)}
              data-testid="save-filters-and-layout"
            >
              <RadioButton
                size={18}
                checked={includeLayout}
                onChange={() => setIncludeLayout(true)}
              />
              <div>
                Filters and table layout
                <small>
                  Also saves the columns shown, their order, pins and widths,
                  the sort, rows per page, and the pivot view and its setup
                </small>
              </div>
            </Choice>
          </Choices>
        </>
      )}
      <Label>This view will apply</Label>
      <Preview>
        <ViewSummary
          query={sendQuery}
          tableState={layout}
          describeQuery={describeQuery}
          describeLayout={tableLayout?.describe}
        />
      </Preview>
      <Label>Sharing</Label>
      <Switch
        label="Share with everyone"
        checked={isPublic}
        onChange={() => setIsPublic(!isPublic)}
        testId="save-view-public"
      />
      <Hint>
        {isPublic
          ? "Everyone who can open this page can find and apply this view."
          : "Only you can see this view. You can share it later from Saved Views."}
      </Hint>
      {errors.main && <Callout mt mb type="error" text={errors.main} />}
      <Modal.Footer>
        <Button mr variant="secondary" text="Cancel" onClick={close} />
        <Button
          text={saving ? "Saving..." : "Save"}
          disabled={saving}
          onClick={submit}
        />
      </Modal.Footer>
    </>
  );
};

export default SaveFilterModal;
